import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { MaxTemperature } from '@models';
import { BaseCardComponent } from '../../atoms/cards/base-card/base-card.component';
import { ActionCardIconComponent } from '../../atoms/cards/action-card/action-card-icon.component';
import { BadgeComponent } from '../../atoms/badge/badge.component';
import { truncate } from '@ui';

@Component({
  selector: 'ui-max-temperature-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    BaseCardComponent,
    ActionCardIconComponent,
    BadgeComponent,
  ],
  template: `
    <ng-container *transloco="let t">
      <ui-base-card
        [class]="'justify-between !items-start gap-y-3'"
        [reverse]="true"
      >
        <ui-action-card-icon icon class="{{ iconClass() }}" [icon]="icon()" />
        <ng-container content>
          <div class="flex flex-col gap-y-3">
            <div class="flex gap-x-11">
              @for (item of alarmsItems(); track $index) {
                <div [class]="'flex flex-col'">
                  <span class="text-xs text-gray-400 font-medium">
                    {{ item.label }}
                  </span>
                  <span class="text-2xl text-orange">
                    {{ item?.count ? item.count + 'º' : '--' }}
                  </span>
                </div>
              }
            </div>
            <h1 class="text-md">
              {{ t(label()) }}
            </h1>
          </div>
        </ng-container>
        <ng-container footer>
          <div class="flex w-full gap-x-1">
            @if (truncatedRsabtName()) {
              <ui-badge
                [class]="truncatedTsabtName() ? 'basis-1/2 cursor-pointer' : 'w-full cursor-pointer'"
                [color]="'orange'"
                [icon]="'unordered-list'"
                (click)="handleRsabtClick()"
                [tooltipBody]="rsabtName()"
              >
                {{ truncatedRsabtName() }}
              </ui-badge>
            }
            @if (truncatedTsabtName()) {
              <ui-badge
                [class]="truncatedRsabtName() ? 'basis-1/2 cursor-pointer' : 'w-full cursor-pointer'"
                [color]="'orange'"
                [icon]="'unordered-list'"
                (click)="handleTsabtClick()"
                [tooltipBody]="tsabtName()"
              >
                {{ truncatedTsabtName() }}
              </ui-badge>
            }
          </div>
        </ng-container>
      </ui-base-card>
    </ng-container>
  `,
})
export class MaxTemperatureCardComponent {
  protected readonly TRUNCATE_MAX_CHARS: number = 30;
  private translocoService = inject(TranslocoService);

  maxTemperatureData = input<MaxTemperature | null>(null);

  // Computed signals que se actualizan automáticamente cuando maxTemperatureData cambia
  remoteCount = computed(() => {
    const data = this.maxTemperatureData();
    if (!data?.rsabt?.rsabtMaxTemperature) return 0;
    return Math.floor(data.rsabt.rsabtMaxTemperature);
  });
  cardCount = computed(() => {
    const data = this.maxTemperatureData();
    if (!data?.tsabt?.tsabtMaxTemperature) return 0;
    return Math.floor(data.tsabt.tsabtMaxTemperature);
  });
  rsabtName = computed(
    () => this.maxTemperatureData()?.rsabt?.transformerName ?? '',
  );
  tsabtName = computed(() => this.maxTemperatureData()?.tsabt?.lineName ?? '');

  truncatedRsabtName = computed(() => {
    const part1 = this.maxTemperatureData()?.rsabt?.transformerId || '';
    const part2 = this.rsabtName() || '';
    const name = !part1 && !part2 ? '---' : `${part1} - ${part2}`;
    return name ? truncate(name, this.TRUNCATE_MAX_CHARS) : '';
  });
  truncatedTsabtName = computed(() => {
    const part1 = this.maxTemperatureData()?.tsabt?.lineId || '';
    const part2 = this.tsabtName() || '';
    const name = !part1 && !part2 ? '---' : `${part1} - ${part2}`;
    return name ? truncate(name, this.TRUNCATE_MAX_CHARS) : '';
  });

  sendRsabtClick = output<{
    distributorId: string;
    centerId: string;
    rsabtId: string;
  }>();
  sendTsabtClick = output<{
    distributorId: string;
    centerId: string;
    rsabtId: string;
    tsabtId: string;
  }>();

  alarmsLinkClick = output<void>();

  remoteLabel = toSignal(
    this.translocoService.selectTranslate('INFRAESTRUCTURE.REMOTE'),
  );
  cardLabel = toSignal(
    this.translocoService.selectTranslate('INFRAESTRUCTURE.CARD'),
  );

  iconClass = signal('text-orange bg-orange-light shadow-custom-orange');
  icon = signal<string>('icons:temperature');
  label = signal<string>('DISTRIBUTION_NETWORK.MAX_TEMPERATURE');

  alarmsItems = computed(() => [
    {
      label: this.remoteLabel(),
      count: this.remoteCount(),
    },
    {
      label: this.cardLabel(),
      count: this.cardCount(),
    },
  ]);

  handleAlarmsLinkClick() {
    this.alarmsLinkClick.emit();
  }

  handleRsabtClick() {
    const data = this.maxTemperatureData();
    if (data?.rsabt) {
      this.sendRsabtClick.emit({
        distributorId: data.rsabt.distributorId,
        centerId: data.rsabt.centerId,
        rsabtId: data.rsabt.rsabtId,
      });
    }
  }

  handleTsabtClick() {
    const data = this.maxTemperatureData();
    if (data?.tsabt) {
      this.sendTsabtClick.emit({
        distributorId: data.tsabt.distributorId,
        centerId: data.tsabt.centerId,
        rsabtId: data.tsabt.rsabtId,
        tsabtId: data.tsabt.tsabtId,
      });
    }
  }
}
